import { ModuleResource } from './types'

const DATABASE_NAME = 'dragon-language-cache'
const DATABASE_VERSION = 1
const STORE_NAME = 'language_modules'
const OPEN_TIMEOUT = 300

export interface CachedLanguageModule {
  module: string
  version: string
  resources: ModuleResource
  updatedAt: number
}

/**
 * 可选的 L2 缓存。IndexedDB 不可用时返回 null，调用方应继续走网络与内存 L1。
 */
export class LangCache {
  private databasePromise?: Promise<IDBDatabase | null>
  private disabled = false

  async get(module: string): Promise<CachedLanguageModule | null> {
    const database = await this.getDatabase()
    if (!database) return null

    try {
      return await new Promise<CachedLanguageModule | null>(
        (resolve, reject) => {
          const request = database
            .transaction(STORE_NAME, 'readonly')
            .objectStore(STORE_NAME)
            .get(module)
          request.onsuccess = () => resolve(request.result || null)
          request.onerror = () => reject(request.error)
        },
      )
    } catch {
      this.disable(database)
      return null
    }
  }

  async put(record: CachedLanguageModule): Promise<void> {
    const database = await this.getDatabase()
    if (!database) return

    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readwrite')
        transaction.objectStore(STORE_NAME).put(record)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(transaction.error)
        transaction.onabort = () => reject(transaction.error)
      })
    } catch {
      this.disable(database)
    }
  }

  private getDatabase(): Promise<IDBDatabase | null> {
    if (this.disabled) return Promise.resolve(null)
    if (!this.databasePromise) {
      this.databasePromise = this.openDatabase()
    }
    return this.databasePromise
  }

  private openDatabase(): Promise<IDBDatabase | null> {
    if (typeof window === 'undefined' || !window.indexedDB) {
      this.disabled = true
      return Promise.resolve(null)
    }

    return new Promise((resolve) => {
      let settled = false
      let timeoutId: number | undefined

      const settle = (database: IDBDatabase | null) => {
        if (settled) {
          database?.close()
          return
        }
        settled = true
        if (timeoutId !== undefined) window.clearTimeout(timeoutId)
        resolve(database)
      }

      timeoutId = window.setTimeout(() => {
        this.disabled = true
        settle(null)
      }, OPEN_TIMEOUT)

      try {
        const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
        request.onupgradeneeded = () => {
          const database = request.result
          if (!database.objectStoreNames.contains(STORE_NAME)) {
            database.createObjectStore(STORE_NAME, { keyPath: 'module' })
          }
        }
        request.onsuccess = () => settle(request.result)
        request.onerror = () => {
          this.disabled = true
          settle(null)
        }
        request.onblocked = () => {
          this.disabled = true
          settle(null)
        }
      } catch {
        this.disabled = true
        settle(null)
      }
    })
  }

  private disable(database?: IDBDatabase) {
    this.disabled = true
    database?.close()
  }
}
